import React, { useMemo, useState } from "react";
import { CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { DatePicker, Input, Select } from "antd";
import dayjs from "dayjs";

const KIND = {
  VID: "vid",
  USER: "userName"
};

const PERIOD_KIND = "period";

const KIND_ITEMS = [
  { key: KIND.VID, label: "Событие" },
  { key: KIND.USER, label: "Пользователь" }
];

const VALUE_WIDTH = {
  [KIND.VID]: 260,
  [KIND.USER]: 160
};

const isActive = (value) => value != null && value !== "";

const labelOf = (options, raw) => {
  const found = (options || []).find((o) => String(o.value) === String(raw));
  return found?.label ?? String(raw);
};

const MiniSelect = ({
  autoFocus,
  defaultOpen,
  disabled,
  options,
  placeholder,
  value,
  onChange,
  width
}) => (
  <Select
    autoFocus={autoFocus}
    defaultOpen={defaultOpen}
    disabled={disabled}
    showSearch
    optionFilterProp="label"
    placeholder={placeholder}
    value={value}
    options={options}
    popupMatchSelectWidth={false}
    variant="borderless"
    suffixIcon={null}
    style={{ width }}
    onChange={onChange}
  />
);

const toDayjs = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const fmtD = (value) => {
  const d = toDayjs(value);
  return d ? d.format("DD.MM.YYYY") : "";
};

/**
 * Поиск и чипы фильтров журнала действий: период / событие / пользователь.
 * Адаптировано из DocExpsComboFilter.
 */
const UserMonitorComboFilter = ({
  value,
  vidOptions,
  userOptions,
  placeholder,
  periodDisabled = false,
  onChange
}) => {
  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKind, setEditKind] = useState(null);

  const today = dayjs().format("YYYY-MM-DD");
  const periodIsToday = value.dateBeg === today && value.dateEnd === today;
  const periodLabel =
    value.dateBeg === value.dateEnd
      ? fmtD(value.dateBeg)
      : `${fmtD(value.dateBeg)} – ${fmtD(value.dateEnd)}`;

  const valueOptionsByKind = useMemo(
    () => ({
      [KIND.VID]: vidOptions || [],
      [KIND.USER]: userOptions || []
    }),
    [vidOptions, userOptions]
  );

  const chips = useMemo(() => {
    const items = [];
    if (isActive(value.vid)) {
      items.push({
        key: KIND.VID,
        kindLabel: "Событие",
        valueLabel: labelOf(vidOptions, value.vid)
      });
    }
    if (isActive(value.userName)) {
      items.push({
        key: KIND.USER,
        kindLabel: "Пользователь",
        valueLabel: labelOf(userOptions, value.userName)
      });
    }
    return items;
  }, [value.vid, value.userName, vidOptions, userOptions]);

  const used = new Set(chips.map((c) => c.key));
  const availableKinds = KIND_ITEMS.filter((k) => !used.has(k.key)).map((k) => ({
    value: k.key,
    label: k.label
  }));
  const canAdd = availableKinds.length > 0;

  const applyKindValue = (kind, nextValue) => {
    onChange({ [kind]: nextValue || null });
  };

  const clearKind = (kind) => {
    applyKindValue(kind, null);
    if (editKind === kind) setEditKind(null);
  };

  const startAdd = () => {
    setEditKind(null);
    setDraftKind(null);
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
  };

  const pickDraftValue = (next) => {
    if (!draftKind) return;
    applyKindValue(draftKind, next);
    cancelAdd();
  };

  return (
    <div className="user-monitor-combo" role="group" aria-label="Поиск и фильтры журнала">
      <span className="user-monitor-combo__search-icon">
        <SearchOutlined />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="user-monitor-combo__search-input"
        value={value.q || ""}
        placeholder={placeholder}
        aria-label="Поиск"
        onChange={(e) => onChange({ q: e.target.value })}
      />
      {!periodDisabled ? (
        editKind === PERIOD_KIND ? (
          <span className="user-monitor-combo__draft">
            <span className="user-monitor-combo__chip-kind">Период</span>
            <DatePicker
              variant="borderless"
              suffixIcon={null}
              allowClear={false}
              format="DD.MM.YYYY"
              size="small"
              style={{ width: 104 }}
              autoFocus
              defaultOpen
              aria-label="Дата с"
              value={toDayjs(value.dateBeg)}
              onChange={(d) => {
                if (!d) return;
                const dateBeg = d.format("YYYY-MM-DD");
                onChange({
                  dateBeg,
                  ...(dateBeg > value.dateEnd ? { dateEnd: dateBeg } : {})
                });
              }}
            />
            <span className="user-monitor-combo__range-sep">–</span>
            <DatePicker
              variant="borderless"
              suffixIcon={null}
              allowClear={false}
              format="DD.MM.YYYY"
              size="small"
              style={{ width: 104 }}
              aria-label="Дата по"
              value={toDayjs(value.dateEnd)}
              onChange={(d) => {
                if (!d) return;
                const dateEnd = d.format("YYYY-MM-DD");
                onChange({
                  dateEnd,
                  ...(dateEnd < value.dateBeg ? { dateBeg: dateEnd } : {})
                });
                setEditKind(null);
              }}
            />
            <button
              type="button"
              className="user-monitor-combo__chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKind(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span className="user-monitor-combo__chip">
            <button
              type="button"
              className="user-monitor-combo__chip-btn"
              title="Изменить период"
              onClick={() => {
                cancelAdd();
                setEditKind(PERIOD_KIND);
              }}
            >
              <span className="user-monitor-combo__chip-kind">Период</span>
              <span className="user-monitor-combo__chip-value">{periodLabel}</span>
            </button>
            {!periodIsToday ? (
              <button
                type="button"
                className="user-monitor-combo__chip-clear"
                title="Сегодня"
                aria-label="Сбросить: Период"
                onClick={() => onChange({ dateBeg: today, dateEnd: today })}
              >
                <CloseOutlined />
              </button>
            ) : null}
          </span>
        )
      ) : null}
      {chips.map((chip) =>
        editKind === chip.key ? (
          <span key={chip.key} className="user-monitor-combo__draft">
            <span className="user-monitor-combo__chip-kind">{chip.kindLabel}</span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={valueOptionsByKind[chip.key]}
              placeholder="Значение"
              value={value[chip.key]}
              width={VALUE_WIDTH[chip.key]}
              onChange={(next) => {
                applyKindValue(chip.key, next);
                setEditKind(null);
              }}
            />
            <button
              type="button"
              className="user-monitor-combo__chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKind(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="user-monitor-combo__chip">
            <button
              type="button"
              className="user-monitor-combo__chip-btn"
              title="Изменить значение"
              onClick={() => {
                cancelAdd();
                setEditKind(chip.key);
              }}
            >
              <span className="user-monitor-combo__chip-kind">{chip.kindLabel}</span>
              <span className="user-monitor-combo__chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="user-monitor-combo__chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearKind(chip.key)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="user-monitor-combo__draft">
          <MiniSelect
            autoFocus={!draftKind}
            defaultOpen={!draftKind}
            options={availableKinds}
            placeholder="Вид фильтра"
            value={draftKind}
            width={130}
            onChange={(kind) => setDraftKind(kind)}
          />
          <MiniSelect
            key={draftKind || "value"}
            autoFocus={Boolean(draftKind)}
            defaultOpen={Boolean(draftKind)}
            disabled={!draftKind}
            options={draftKind ? valueOptionsByKind[draftKind] || [] : []}
            placeholder="Значение"
            width={draftKind ? VALUE_WIDTH[draftKind] || 160 : 160}
            onChange={pickDraftValue}
          />
          <button
            type="button"
            className="user-monitor-combo__chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding ? (
        <button
          type="button"
          className="user-monitor-combo__add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

export default UserMonitorComboFilter;
